import _, { Dictionary } from 'lodash';
import { useHistory } from 'react-router-dom';
import React, {
    useState, useEffect, ChangeEvent, useMemo,
} from 'react';
import { Alert, Button, Checkbox } from '@cimpress/react-components';
import * as colors from '@cimpress/react-components/lib/colors';
import { Slider } from '@material-ui/core';
import { TargetProductSpecification } from '../templateGeneration/TargetProductSpecification';
import RejectJobConfirmationModal from '../templateGeneration/rejectJobConfirmationModal';
import ResizeTemplateJobLabel from '../templateGeneration/templateGenerationJobLabel';
import PreviewGroupsBySourceDesign from './PreviewGroupsBySourceDesign';
import { PreviewGenerationResult } from './previewGenerationResultData';
import batchGetDps from '../../services/dpsService';
import getProductName from '../../services/productService';
import { getDpsDisplayText } from '../../utilityHelpers';
import { ApprovedPreviewConfiguration } from '../../services/rtgService';
import '../templateGeneration/reviewAndApprove.scss';

interface Props {
    previewGenerationResults: PreviewGenerationResult[];
    onPreviewConfirmation:
    (approvedPreviewConfigurations: ApprovedPreviewConfiguration[]) => void;
}

const ResizeTemplatePreview = ({ previewGenerationResults, onPreviewConfirmation }: Props): JSX.Element => {
    const [wasPageReviewed, setWasPageReviewed] = useState(false);
    const [isRejectionModalVisible, setIsRejectionModalVisible] = useState(false);
    const [jobItemIds, setJobItemIds] = useState<string[]>([]);
    const [finalPreviewResults, setFinalPreviewResults] = useState<Dictionary<PreviewGenerationResult[]>>();
    const [showSelectAllWarning, setShowSelectAllWarning] = useState(false);
    const history = useHistory();
    const [invalidTemplates, setInvalidTemplates] = useState<string[]>([]);

    // Named as JobItems in anticipation of when we are working with JobItems.
    // For now, these are actually ensembleLines, with a meaningless identifier to represent them
    const [selectedJobItems, setSelectedJobItems] = useState<string[]>([]);
    const [width, setWidth] = useState(200);

    useEffect(() => {
        if (finalPreviewResults) {
            /* eslint-disable-next-line */
            const groups = Object.entries(finalPreviewResults).flatMap(([sourceTT, previewGroups]) => previewGroups);
            const ids = groups.map((x) => x.uniqueIdentifier as string);

            setJobItemIds(ids);
        }
    }, [finalPreviewResults]);

    const validateTemplates = useMemo(() => (results: Dictionary<PreviewGenerationResult[]>): string[] => {
        if (!results) return [];
        return Object.values(results)
            .flat()
            .filter((result) => !result.target?.templateName?.trim())
            .map((result) => result.uniqueIdentifier as string);
    }, []);

    useEffect(() => {
        setInvalidTemplates(validateTemplates(finalPreviewResults || {}));
    }, [finalPreviewResults, validateTemplates]);
    useEffect(() => {
        async function getDpsDataFromApi(): Promise<Map<string, string>> {
            // Fetch DPS data for each prominent ensemble to display Trim data and substrate color
            // Assumes same DPS for each panel in the prominent variant ensemble
            const dpsIds = previewGenerationResults.map((ensembleLine) => ensembleLine.ensembles
                .find(((e) => e.isProminentVariant === true))?.panels[0].designPhysicalSpecId);
            const uniqueDpsIds = [...new Set(dpsIds)];
            const dpsData = await batchGetDps(uniqueDpsIds as string[]);
            const trimMap = new Map<string, string>();

            dpsData.forEach((dps) => {
                trimMap.set(dps.id, getDpsDisplayText(dps));
            });
            return trimMap;
        }

        async function getProductDataFromApi(): Promise<Map<string, string>> {
            // Fetch each Product Name for each PRD to display
            const prds = previewGenerationResults.map((ensembleLine) => ensembleLine.target.product);
            const uniquePrds = [...new Set(prds)];
            const promises = uniquePrds.map((prd) => getProductName(prd));

            /* eslint-disable no-param-reassign */
            return Promise.all(promises).then((results) => {
                // Union all of the maps together
                const mergedMap = results.reduce((finalMap: Map<string, string>, oneResult) => {
                    if (oneResult) {
                        finalMap = new Map([...oneResult, ...finalMap]);
                    }
                    return finalMap;
                }, new Map<string, string>());

                return mergedMap;
            }).catch(() => new Map<string, string>());
            /* eslint-enable no-param-reassign */
        }

        // Adds the unique ID, Trim values and product name for display in the preview group. Also groups according to source TT
        /* eslint-disable-next-line */

        async function setPreviewGroupMetadata(dpsMapPromise: Promise<Map<string, string>>,
            productMapPromise: Promise<Map<string, string>>): Promise<void> {
            const dpsMap = await dpsMapPromise;
            const productNameMap = await productMapPromise;
            const generationResultsWithMetadata = previewGenerationResults.map((ensembleLine, index) => {
                const completeEnsembleLine = ensembleLine;
                const prominentVariantDps = completeEnsembleLine.ensembles
                    .find(((e) => e.isProminentVariant === true))?.panels[0].designPhysicalSpecId || '';

                completeEnsembleLine.uniqueIdentifier = index.toString();
                completeEnsembleLine.dpsTrim = dpsMap.get(prominentVariantDps);
                completeEnsembleLine.productName = productNameMap.get(completeEnsembleLine.target.product);
                return completeEnsembleLine;
            });

            const groupedBySourceTemplate = _.groupBy(generationResultsWithMetadata, 'sourceTemplateToken');

            setFinalPreviewResults(groupedBySourceTemplate);
        }
        const dpsDataMap = getDpsDataFromApi();
        const productDataMap = getProductDataFromApi();

        setPreviewGroupMetadata(dpsDataMap, productDataMap);
    }, [previewGenerationResults]);

    // Add or remove jobItems from the list of approved ids as they are selected
    const onJobItemSelected = (ids: string[], isSelected: boolean): void => {
        if (isSelected) {
            setSelectedJobItems([...new Set([...selectedJobItems, ...ids])]);
        } else {
            setSelectedJobItems([...new Set(selectedJobItems.filter((selectedJobItem) => !ids.includes(selectedJobItem)))]);
        }
    };

    // Final function on this page which kicks off the generation request
    const onContinue = (): void => {
        const approvedTokenTargetCombinations: Map<string, TargetProductSpecification[]> = new Map();

        selectedJobItems.forEach((ji: string) => {
            // Find the result by uniqueIdentifier from finalPreviewResults
            const result = Object.values(finalPreviewResults || {})
                .flat()
                .find((r) => r.uniqueIdentifier === ji);

            if (!result) return;

            // Update the target's name to ensure it's captured
            const updatedTarget: TargetProductSpecification = {
                ...result.target,
                templateName: result.target?.templateName?.trim() || '', // Fallback to empty if not set
            };

            const mapEntry = approvedTokenTargetCombinations.get(result.sourceTemplateToken);

            if (mapEntry) {
                mapEntry.push(updatedTarget);
            } else {
                approvedTokenTargetCombinations.set(result.sourceTemplateToken, [updatedTarget]);
            }
        });

        const approvedConfigurationsForGeneration: ApprovedPreviewConfiguration[] = Array.from(
            approvedTokenTargetCombinations,
            ([sourceTemplateToken, targets]) => ({
                sourceTemplateToken,
                targets,
            }),
        );

        onPreviewConfirmation(approvedConfigurationsForGeneration);
    };

    // If the user rejects the whole job. Redirect to the start of the flow
    const onRejectConfirmed = (): void => {
        setIsRejectionModalVisible(false);
        // Reloads the page. Resize Tool is a single page app so there is nowhere else to redirect to
        history.go(0);
    };

    const onSelectAllOrUnselectAllClicked = (value: boolean): void => {
        if (!jobItemIds) {
            return;
        }

        if (value && invalidTemplates.length > 0) {
            setShowSelectAllWarning(true);
            return;
        }

        setShowSelectAllWarning(false);
        onJobItemSelected(jobItemIds, value);
    };

    const isFinishDisabled = useMemo(() => (
        wasPageReviewed !== true
        || selectedJobItems.length < 1
        || selectedJobItems.some((id) => invalidTemplates.includes(id))
    ), [wasPageReviewed, selectedJobItems, invalidTemplates]);

    const ValidationMessages = (): JSX.Element => {
        const invalidSelectedCount = selectedJobItems.filter((id) => invalidTemplates.includes(id)).length;

        return (
            <div style={{ margin: '10px 0' }}>
                {invalidSelectedCount > 0 && (
                    <div>
                        {invalidSelectedCount}
                        {' '}
                        selected templates are missing names
                    </div>
                )}
                {invalidTemplates.length > 0 && selectedJobItems.length === 0 && (
                    <div style={{ color: colors.shale }}>
                        {invalidTemplates.length}
                        {' '}
                        templates need names before submission
                    </div>
                )}
            </div>
        );
    };
    const handleTemplateNameChange = (uniqueIdentifier: string, newName: string): void => {
        setFinalPreviewResults((prevResults) => {
            if (!prevResults) return prevResults;

            return _.mapValues(prevResults, (previewGroups) => previewGroups.map((result) => (result.uniqueIdentifier === uniqueIdentifier
                ? { ...result, target: { ...result.target, templateName: newName } }
                : result)));
        });
    };
    // eslint-disable-next-line @typescript-eslint/ban-types
    const handleChangeWidth = (e: ChangeEvent<{}>, newValue: number | number[]): void => setWidth(newValue as number);

    return (
        <div>
            {showSelectAllWarning && (
                <Alert
                    dismissible
                    status="warning"
                    message={`Cannot select all - ${invalidTemplates.length} templates are missing names`}
                    onDismiss={(): void => setShowSelectAllWarning(false)}
                />
            )}
            <div className="job-details-header">
                { /* TODO: Add Job ID header here during phase 2 */}
                <h3>
                    Job
                </h3>
                <ResizeTemplateJobLabel status="InReview" />
            </div>

            <div className="job-details-subheader">
                {/* eslint-disable-next-line */}
                Check all primary configurations to be generated. If there are issues with the resize, leave the configuration unchecked. Any unchecked configurations will not be created and the ensemble line will not be generated.
            </div>

            <div className="composed-previews-section">
                <div className="composed-previews-section-buttons-group">
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                        <span style={{ fontSize: 12 }}>Preview size, px:</span>
                        <Slider
                            style={{ width: 200 }}
                            min={100}
                            max={500}
                            step={100}
                            value={width}
                            valueLabelDisplay="auto"
                            onChange={handleChangeWidth}
                        />
                    </div>
                    <Button onClick={(): void => onSelectAllOrUnselectAllClicked(false)}>Unselect All</Button>
                    <Button onClick={(): void => onSelectAllOrUnselectAllClicked(true)}>Select All</Button>
                </div>
            </div>

            <div>
                {/* eslint-disable-next-line */}
                {finalPreviewResults && Object.entries(finalPreviewResults).map(([sourceTT, previewGroups]) => (<PreviewGroupsBySourceDesign
                    key={sourceTT}
                    previewGroups={previewGroups}
                    selectedJobItems={selectedJobItems}
                    width={width}
                    onJobItemSelected={onJobItemSelected}
                    onTemplateNameChange={handleTemplateNameChange}
                />))}
            </div>
            <ValidationMessages />
            <div>
                <Checkbox
                    checked={wasPageReviewed}
                    label="By checking this box I confirm that all previews have been reviewed and that any checked meet Cimpress standards."
                    onChange={(): void => setWasPageReviewed(!wasPageReviewed)}
                />
            </div>
            <div className="approve-buttons-wrapper">
                {/* TODO: During Preview Phase 2, remove the below '|| selectedJobItems.length < 1' constraint. */}
                <Button
                    disabled={isFinishDisabled}
                    className="publish-button"
                    variant="primary"
                    size="lg"
                    onClick={onContinue}
                >
                    Finish
                </Button>
                <small style={{ color: colors.shale, flex: 1 }}>{`${selectedJobItems.length} of ${previewGenerationResults.length} selected`}</small>
                <Button
                    variant="anchor"
                    onClick={(): void => setIsRejectionModalVisible(true)}
                >
                    Reject All
                </Button>
            </div>
            <RejectJobConfirmationModal
                isModalVisible={isRejectionModalVisible}
                isSaving={false}
                onSetModalVisible={setIsRejectionModalVisible}
                onConfirmed={onRejectConfirmed}
            />
        </div>
    );
};

export default ResizeTemplatePreview;
